import { useEffect, useLayoutEffect, useRef, type MutableRefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { createParticleWorld, type ParticleWorld } from './world';
import {
  burstFromDetonation,
  burstFromImpact,
  burstFromResidue,
  demolitionParticleMassScale,
  demolitionParticleWorldCapacities,
  demolitionResidueTint,
  DEMOLITION_PARTICLE_ARENA,
  particleTierForDemolitionEffect,
  type DemolitionParticleController,
} from './demolitionDust';
import type { ProfileId } from '../../services/destructionProfiles';
import type { DemolitionEffectTier } from '../presentation/effectTier';
import type { ArenaBounds, BoxCollider } from './types';

const KEY_LIGHT = { x: 8, y: 14, z: 6 } as const;

function hideLabDecor(scene: THREE.Scene): void {
  const colliders = scene.getObjectByName('particle-lab-colliders');
  if (colliders) colliders.visible = false;
  const edge = scene.getObjectByName('particle-lab-arena-edge');
  if (edge) edge.visible = false;
  const labVoid = scene.getObjectByName('particle-lab-void');
  if (labVoid) labVoid.visible = false;
  for (let i = 0; i < 16; i += 1) {
    const boxtop = scene.getObjectByName(`particle-lab-boxtop-${i}`);
    if (boxtop) boxtop.visible = false;
  }
}

function shaderUniforms(obj: THREE.Object3D | undefined): THREE.ShaderMaterial['uniforms'] | null {
  if (!obj || !('material' in obj)) return null;
  const mat = (obj as THREE.Mesh).material;
  if (!mat || Array.isArray(mat) || !('uniforms' in mat)) return null;
  return (mat as THREE.ShaderMaterial).uniforms;
}

function applyResidueTint(scene: THREE.Scene, material: ProfileId): void {
  const tint = demolitionResidueTint(material);
  const apply = (u: THREE.ShaderMaterial['uniforms'] | null) => {
    if (!u) return;
    if (u.uBrickTint) (u.uBrickTint.value as THREE.Color).setRGB(tint[0], tint[1], tint[2]);
    if (u.uConcreteTint) (u.uConcreteTint.value as THREE.Color).setRGB(tint[0], tint[1], tint[2]);
    if (u.uStoneTint) (u.uStoneTint.value as THREE.Color).setRGB(tint[0], tint[1], tint[2]);
  };
  apply(shaderUniforms(scene.getObjectByName('particle-lab-ground')));
  for (let i = 0; i < 16; i += 1) {
    apply(shaderUniforms(scene.getObjectByName(`particle-lab-boxtop-${i}`)));
  }
}

function styleDepositGround(scene: THREE.Scene): void {
  const ground = scene.getObjectByName('particle-lab-ground') as THREE.Mesh | undefined;
  if (!ground) return;
  const mat = ground.material;
  if (!mat || Array.isArray(mat) || !('uniforms' in mat)) return;
  const shader = mat as THREE.ShaderMaterial;
  const u = shader.uniforms;
  if (u.uDepositOnly) u.uDepositOnly.value = 1;
  if (u.uDisplace) u.uDisplace.value = 0;
  if (u.uGroundColor) u.uGroundColor.value.set('#4a535e');
  if (u.uLightDir) u.uLightDir.value.set(KEY_LIGHT.x, KEY_LIGHT.y, KEY_LIGHT.z).normalize();
  shader.transparent = true;
  shader.depthWrite = false;
  shader.polygonOffset = true;
  shader.polygonOffsetFactor = -1;
  shader.polygonOffsetUnits = -1;
  shader.needsUpdate = true;
  ground.renderOrder = 2;
  applyResidueTint(scene, 'concrete');
}

function bindController(
  world: ParticleWorld,
  scene: THREE.Scene,
  massScale: number,
): DemolitionParticleController {
  return {
    spawnImpact(input) {
      applyResidueTint(scene, input.material);
      const params = burstFromImpact(input, massScale);
      if (params) world.burst(params);
    },
    spawnDetonation(input) {
      applyResidueTint(scene, input.material);
      const params = burstFromDetonation(input, massScale);
      if (params) world.burst(params);
    },
    spawnResidue(input) {
      applyResidueTint(scene, input.material);
      const params = burstFromResidue(input, massScale);
      if (params) world.burst(params);
    },
    reset() {
      world.reset();
    },
  };
}

export function DemolitionParticleLayer({
  effectTier,
  wallCenter,
  wallHalf,
  residueGeneration,
  controllerRef,
  arenaBounds = DEMOLITION_PARTICLE_ARENA,
  colliders,
}: {
  effectTier: DemolitionEffectTier;
  wallCenter?: { readonly x: number; readonly y: number; readonly z: number };
  wallHalf?: { readonly x: number; readonly y: number; readonly z: number };
  residueGeneration: number;
  controllerRef: MutableRefObject<DemolitionParticleController | null>;
  arenaBounds?: ArenaBounds;
  colliders?: readonly BoxCollider[];
}) {
  const { gl, scene, camera } = useThree();
  const worldRef = useRef<ParticleWorld | null>(null);
  const generationRef = useRef(residueGeneration);
  const particleTier = particleTierForDemolitionEffect(effectTier);
  const massScale = demolitionParticleMassScale(effectTier);

  useLayoutEffect(() => {
    if (!particleTier) {
      controllerRef.current = null;
      return undefined;
    }
    const boxes = colliders
      ? colliders.slice()
      : wallCenter && wallHalf
        ? [{
          center: [wallCenter.x, wallCenter.y, wallCenter.z] as const,
          half: [wallHalf.x + 0.05, wallHalf.y + 0.05, wallHalf.z + 0.03] as const,
        }]
        : [];
    const world = createParticleWorld(gl, {
      arenaBounds,
      tier: particleTier,
      chunkFloorOnlySettle: true,
      disableChunks: true,
      ...demolitionParticleWorldCapacities(),
      groundShadow: { splatAmt: 0.08, pointScale: 1.3, maxPoint: 8 },
      colliders: boxes,
    });
    world.attach(scene);
    world.setSixWay(true);
    world.setLightDir(KEY_LIGHT.x, KEY_LIGHT.y, KEY_LIGHT.z);
    hideLabDecor(scene);
    styleDepositGround(scene);
    worldRef.current = world;
    controllerRef.current = bindController(world, scene, massScale);
    if (import.meta.env.DEV) {
      (window as unknown as { __demolitionParticles?: () => unknown }).__demolitionParticles = () =>
        world.getStats();
    }
    return () => {
      world.detach();
      world.dispose();
      worldRef.current = null;
      controllerRef.current = null;
      if (import.meta.env.DEV) {
        delete (window as unknown as { __demolitionParticles?: () => unknown }).__demolitionParticles;
      }
    };
  }, [
    arenaBounds.maxX,
    arenaBounds.maxZ,
    arenaBounds.minX,
    arenaBounds.minZ,
    colliders,
    controllerRef,
    gl,
    massScale,
    particleTier,
    scene,
    wallCenter?.x,
    wallCenter?.y,
    wallCenter?.z,
    wallHalf?.x,
    wallHalf?.y,
    wallHalf?.z,
  ]);

  useEffect(() => {
    if (residueGeneration === generationRef.current) return;
    generationRef.current = residueGeneration;
    worldRef.current?.reset();
  }, [residueGeneration]);

  useFrame((_, rawDt) => {
    const world = worldRef.current;
    if (!world) return;
    try {
      world.step(Math.min(Math.max(rawDt, 0), 0.05), camera);
    } catch {
      // Presentation must never abort the R3F / physics loop.
    }
  });

  return null;
}
